<template>
    <div class="tp-page">
        <!-- ===== 教师身份条 ===== -->
        <div class="tp-idbar">
            <div class="tp-avatar-wrap">
                <span class="tp-avatar-ring"></span>
                <img v-if="teacherInfo.avatar" :src="teacherInfo.avatar" class="tp-avatar-img" />
                <div v-else class="tp-avatar">{{ avatarChar }}</div>
            </div>
            <div class="tp-idmain">
                <div class="tp-name">
                    {{ teacherInfo.name || '未命名教师' }}
                    <span class="tp-account" v-if="teacherAccount">{{ teacherAccount }}</span>
                </div>
                <div class="tp-chips">
                    <span class="tp-chip" v-if="teacherInfo.school">🏫 {{ teacherInfo.school }}</span>
                    <span class="tp-chip sub" v-if="teacherInfo.subject">学科 · {{ teacherInfo.subject }}</span>
                    <span class="tp-chip tmpl" v-if="teacherInfo.teachingYears">教龄 {{ teacherInfo.teachingYears }}</span>
                    <span class="tp-chip lessons">累计评课 {{ teacherInfo.evaluationCount || 0 }} 次</span>
                </div>
            </div>
            <div class="tp-idright">
                <div class="tp-std-tabs">
                    <div class="tp-std-tab" v-for="(tab, index) in standardTabs" :key="index"
                        :class="{ active: activeTab === index }" @click="switchTab(index)">
                        {{ tab.name }}
                    </div>
                    <div class="tp-tabs-empty" v-if="templatesLoading">
                        <i class="el-icon-loading"></i>
                        <span>评价标准加载中...</span>
                    </div>
                    <div class="tp-tabs-empty" v-else-if="standardTabs.length === 0">
                        <i class="el-icon-warning-outline"></i>
                        <span>{{ templatesError || '暂无评价标准' }}</span>
                    </div>
                </div>
                <div class="tp-update">数据更新于 <b>{{ updateTime }}</b></div>
            </div>
        </div>

        <!-- ===== KPI 四卡 ===== -->
        <div class="tp-kpis">
            <div class="tp-kpi" style="--c:#4fc3ff;">
                <div class="ring">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4fc3ff" stroke-width="2"><path d="M4 19V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14l-8-4-8 4z"/><path d="M9 8h6"/></svg>
                </div>
                <div class="info">
                    <div class="label">累计评课</div>
                    <div class="value">{{ kpiReviewCount }}<small>次</small></div>
                    <div class="trend">参与画像 {{ (historyData.xAxis || []).length }} 次记录</div>
                </div>
            </div>
            <div class="tp-kpi" style="--c:#35e0b0;">
                <div class="ring">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#35e0b0" stroke-width="2"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></svg>
                </div>
                <div class="info">
                    <div class="label">综合得分</div>
                    <div class="value">{{ kpiLatest ? fmt(kpiLatest) : '—' }}</div>
                    <div class="trend">最近一次评课得分</div>
                </div>
            </div>
            <div class="tp-kpi" style="--c:#ffd24a;">
                <div class="ring">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffd24a" stroke-width="2"><path d="M12 2l2.9 6.3 6.9.6-5.2 4.6 1.5 6.8L12 16.9 5.9 20.3l1.5-6.8L2.2 8.9l6.9-.6z"/></svg>
                </div>
                <div class="info">
                    <div class="label">个人最高分</div>
                    <div class="value">{{ kpiBest ? fmt(kpiBest) : '—' }}</div>
                    <div class="trend">历史最优记录</div>
                </div>
            </div>
            <div class="tp-kpi" style="--c:#a78bfa;">
                <div class="ring">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" stroke-width="2"><path d="M16 3h5v5M21 3l-7 7M8 21H3v-5M3 21l7-7"/></svg>
                </div>
                <div class="info">
                    <div class="label">同伴均值对比</div>
                    <div class="value">{{ kpiPeerDiffText }}</div>
                    <div class="trend">{{ kpiPeerTrend }}</div>
                </div>
            </div>
        </div>

        <!-- ===== 主区三栏 ===== -->
        <div class="tp-grid">
            <!-- 左栏 -->
            <div class="tp-col">
                <div class="tp-card">
                    <div class="tp-sectitle">教师基本信息</div>
                    <div class="tp-info-grid">
                        <div class="tp-info-cell"><div class="k">姓名</div><div class="v plain">{{ teacherInfo.name || '—' }}</div></div>
                        <div class="tp-info-cell"><div class="k">学科</div><div class="v plain">{{ teacherInfo.subject || '—' }}</div></div>
                        <div class="tp-info-cell"><div class="k">教龄</div><div class="v">{{ teacherInfo.teachingYears || '—' }}</div></div>
                        <div class="tp-info-cell"><div class="k">评课次数</div><div class="v">{{ teacherInfo.evaluationCount || 0 }} 次</div></div>
                        <div class="tp-info-cell wide"><div class="k">所在学校</div><div class="v plain">{{ teacherInfo.school || '—' }}</div></div>
                    </div>
                </div>

                <div class="tp-card tp-flex1">
                    <div class="tp-sectitle" style="--accent:#35e0b0;">总体评价<span class="more">AI 生成</span></div>
                    <div class="tp-eval-text" v-if="overallEvaluationLoading">
                        <i class="el-icon-loading"></i> 评价加载中...
                    </div>
                    <div class="tp-eval-text" v-else>{{ overallEvaluation || '暂无评价内容' }}</div>
                </div>
            </div>

            <!-- 中栏 -->
            <div class="tp-col">
                <div class="tp-card">
                    <div class="tp-sectitle" style="--accent:#4fc3ff;">核心能力画像<span class="more">按评价标准维度</span></div>
                    <div class="tp-chart" ref="radarChart"></div>
                    <div class="tp-legend">
                        <span class="item"><i class="lc lc-personal"></i>个人记录</span>
                        <span class="item"><i class="lc lc-best"></i>最优记录</span>
                        <span class="item"><i class="lc lc-avg"></i>平均记录</span>
                    </div>
                </div>

                <div class="tp-card tp-flex1">
                    <div class="tp-sectitle" style="--accent:#ffd24a;">历史与同伴教师对比<span class="more" @click="showHistoryFilter = !showHistoryFilter">维度筛选 ›</span></div>
                    <div class="tp-history-wrap">
                        <div class="tp-chart tp-chart-history" ref="historyChart"></div>
                        <div class="tp-filter-overlay" v-show="showHistoryFilter">
                            <div class="tp-filter-title">
                                <span>{{ historyFilterTitle }}</span>
                                <i class="el-icon-close tp-filter-close" @click="showHistoryFilter = false"></i>
                            </div>
                            <div v-if="historyDimLoading" class="tp-dim-loading">
                                <i class="el-icon-loading"></i> 加载中...
                            </div>
                            <template v-else>
                                <div v-if="historyCurrentDimList.length === 0" class="tp-dim-empty">暂无维度数据</div>
                                <div v-else class="tp-dim-list">
                                    <div v-for="dim in historyCurrentDimList" :key="dim.id" class="tp-dim-item"
                                        :class="{ active: historyCurrentDimId === String(dim.id) }" @click="selectHistoryDim(dim)">
                                        <span class="dim-name">{{ dim.name }}</span>
                                        <i v-if="dim.children && dim.children.length" class="el-icon-arrow-right dim-arrow"></i>
                                    </div>
                                </div>
                                <div v-if="historyNavStack.length > 0" class="tp-filter-back" @click="goBackHistoryDim">
                                    <i class="el-icon-arrow-left"></i> 返回上一级
                                </div>
                            </template>
                        </div>
                    </div>
                    <div class="tp-legend">
                        <span class="item"><i class="lc lc-personal"></i>个人记录</span>
                        <span class="item"><i class="lc lc-best"></i>最优记录</span>
                        <span class="item"><i class="lc lc-avg"></i>平均记录</span>
                    </div>
                </div>
            </div>

            <!-- 右栏 -->
            <div class="tp-col">
                <div class="tp-card tp-flex1">
                    <div class="tp-sectitle" style="--accent:#a78bfa;">智能个性化指导<span class="more">AI 助手</span></div>
                    <div class="tp-ai-wrap">
                        <div class="tp-ai-stage">
                            <img v-show="!isVideoPlaying" src="@/assets/image/ai/Ai2.png" alt="智能助手" class="tp-ai-img" />
                            <img v-show="isVideoPlaying" src="@/assets/image/ai/AiTalk2.gif" alt="智能助手说话" class="tp-ai-img" />
                            <span class="tp-ai-badge">● 在线</span>
                        </div>
                        <div class="tp-chat-area">
                            <div class="tp-chat-messages" ref="chatMessages">
                                <div class="tp-msg" v-for="(msg, index) in chatMessages" :key="index"
                                    :class="{ user: msg.role === 'user' }">
                                    <div class="m-avatar">
                                        <img v-if="msg.role === 'ai'" src="../../assets/image/teacher_portrait/ai-robot-icon.png" class="m-avatar-img" />
                                        <span v-else>我</span>
                                    </div>
                                    <div v-if="msg.role === 'ai' && msg.loading && !(msg.displayText || msg.content)"
                                        class="m-body">思考中，请稍候...</div>
                                    <div v-else class="m-body">{{ msg.displayText || msg.content }}</div>
                                </div>
                            </div>
                            <div class="tp-chat-input">
                                <el-input v-model="chatInput" placeholder="请输入您的问题" size="small"
                                    @keyup.enter.native="sendMessage"></el-input>
                                <el-button type="primary" size="small" @click="sendMessage">发送</el-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'
import { getTeacherTemplates, getTeacherProfile, getTeacherCompare, getTeacherEvaluation, getTemplateDimensions, getTeacherAbility } from '@/request/api/teacher.js'
import { TEACHER_GUIDE_SYSTEM_PROMPT_CORE, renderPrompt } from '@/global/prompts.js'
export default {
    name: 'TeacherPortraitNew',
    components: {
    },
    data() {
        return {
            teacher_id: '',
            lesson_id: '',
            activeTab: 0,
            standardTabs: [],
            templatesLoading: false,
            templatesError: '',
            updateTime: '2025-12-9 09:22',
            teacherInfo: {
                name: '',
                subject: '',
                teachingYears: '',
                school: '',
                evaluationCount: 0,
                avatar: '',
                account: ''
            },
            defaultAvatar: '',
            // 弹窗显示控制
            showHistoryFilter: false,
            showRadarFilter: false,
            // 历史对比图表数据（默认占位，接口返回后覆盖）
            historyData: {
                xAxis: ['第一次', '第二次', '第三次', '第四次', '最近一次'],
                personal: [78, 82, 85, 80, 88],
                best: [85, 88, 90, 92, 95],
                average: [72, 75, 78, 76, 80]
            },
            // 一级维度筛选（历史对比与核心能力画像共享）
            primaryFilter: {
                personal: true,
                best: true,
                average: true
            },
            // 核心能力画像雷达图数据（由 ability 接口返回）
            radarDimensions: [],
            // 总体评价
            overallEvaluation: '',
            overallEvaluationLoading: false,
            // 聊天
            chatMessages: [
                {
                    role: 'ai',
                    content: '您好，我是个性化指导智能体，希望能够为您的教学提供帮助！'
                }
            ],
            chatInput: '',
            chatLoading: false,
            chatCid: null,
            systemPromptSent: false,
            // 智能体图片动画 + 语音合成 + 打字机效果（与议课页 screen_dialogue.vue 保持一致）
            isAISpeaking: false,
            isVideoPlaying: false,
            speechSynthesis: null,
            currentUtterance: null,
            speechBuffer: '',
            isPlayingSpeech: false,
            speechQueue: [],
            availableVoices: [],
            maleVoiceReady: null,
            // 图表实例
            historyChartInstance: null,
            radarChartInstance: null,
            // 历史对比筛选-维度导航
            historyDimTree: [],
            historyDimLoading: false,
            historyNavStack: [],
            historyCurrentDimId: ''
        }
    },
    computed: {
        historyFilterTitle() {
            const levels = ['一级', '二级', '三级', '四级', '五级']
            const idx = this.historyNavStack.length
            return (levels[idx] || (idx + 1) + '级') + '维度'
        },
        historyCurrentDimList() {
            if (this.historyNavStack.length === 0) return this.historyDimTree
            const lastId = this.historyNavStack[this.historyNavStack.length - 1].id
            return this.findDimChildrenById(lastId, this.historyDimTree) || []
        },
        // ===== 顶栏身份条 / KPI =====
        avatarChar() {
            const n = (this.teacherInfo && this.teacherInfo.name) || ''
            return n ? n.slice(0, 1) : '师'
        },
        teacherAccount() {
            return (this.teacherInfo && this.teacherInfo.account) || ''
        },
        personalScores() {
            return (this.historyData.personal || []).filter(v => typeof v === 'number' && v > 0)
        },
        kpiReviewCount() {
            return (this.teacherInfo && this.teacherInfo.evaluationCount) || 0
        },
        kpiLatest() {
            const arr = this.personalScores
            return arr.length ? arr[arr.length - 1] : 0
        },
        kpiBest() {
            const arr = this.personalScores
            return arr.length ? Math.max.apply(null, arr) : 0
        },
        peerAvg() {
            const arr = (this.historyData.average || []).filter(v => typeof v === 'number' && v > 0)
            return arr.length ? arr[0] : 0
        },
        kpiPeerDiff() {
            if (!this.peerAvg || !this.kpiLatest) return 0
            return Math.round((this.kpiLatest - this.peerAvg) * 10) / 10
        },
        kpiPeerDiffText() {
            if (!this.kpiPeerDiff) return '—'
            const v = Math.abs(this.kpiPeerDiff)
            return (this.kpiPeerDiff > 0 ? '+' : '-') + this.fmt(v) + '分'
        },
        kpiPeerTrend() {
            if (!this.peerAvg) return '暂无同伴均值数据'
            if (!this.kpiPeerDiff) return '与同伴均值持平（' + this.fmt(this.peerAvg) + '）'
            return (this.kpiPeerDiff > 0 ? '高于' : '低于') + '同伴均值 ' + this.fmt(this.peerAvg) + ' 分'
        }
    },
    created() {
        this.teacher_id = this.$route.query.teacher_id || ''
        this.lesson_id = this.$route.query.lesson_id || ''
        // 初始化语音合成（与议课页保持一致）
        this.speechSynthesis = window.speechSynthesis || null
        this.speechBuffer = ''
        this.speechQueue = []
        this.isPlayingSpeech = false
        if (this.speechSynthesis) {
            this.loadAndCacheVoices()
            this.speechSynthesis.onvoiceschanged = () => {
                this.loadAndCacheVoices()
            }
        }
        this.loadTemplates()
        this.loadProfile()
    },
    watch: {
        primaryFilter: {
            deep: true,
            handler() {
                this.updateHistoryChart()
                this.updateRadarChart()
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.initHistoryChart()
            this.initRadarChart()
            // 延迟 resize 确保 flex 布局计算完成
            setTimeout(() => {
                this.handleResize()
            }, 200)
        })
    },
    beforeDestroy() {
        if (this.historyChartInstance) {
            this.historyChartInstance.dispose()
        }
        if (this.radarChartInstance) {
            this.radarChartInstance.dispose()
        }
        // 页面销毁时停止语音播放
        this.stopSpeaking()
        window.removeEventListener('resize', this.handleResize)
    },
    methods: {
        switchTab(index) {
            this.activeTab = index
            // 切换评价标准后重置维度导航，并重新加载数据
            this.historyNavStack = []
            this.historyCurrentDimId = ''
            this.loadOverallEvaluation()
            this.loadHistoryCompare()
            this.loadDimensionsTree()
            this.loadRadarAbility()
        },
        loadProfile() {
            if (!this.teacher_id) return
            getTeacherProfile(this.teacher_id).then(res => {
                if (res && res.code === 0 && res.data) {
                    const d = res.data
                    const subjects = Array.isArray(d.subjects) ? d.subjects.filter(Boolean).join('、') : (d.subjects || '')
                    this.teacherInfo = {
                        name: d.name || '',
                        subject: subjects,
                        teachingYears: d.teachingYears > 0 ? (d.teachingYears + '年') : '',
                        school: d.schoolName || '',
                        evaluationCount: d.reviewCount || 0,
                        avatar: d.avatar || '',
                        account: d.account || d.username || ''
                    }
                }
            }).catch(err => {
                console.error('获取教师基本信息失败', err)
            })
        },
        // 获取总体评价（传入当前选中的评价标准 templateId）
        loadOverallEvaluation() {
            if (!this.teacher_id) return
            const currentTab = this.standardTabs[this.activeTab]
            if (!currentTab || !currentTab.id) {
                this.overallEvaluation = ''
                return
            }
            console.log('[loadOverallEvaluation] 请求参数: teacherId=', this.teacher_id, 'templateId=', currentTab.id)
            this.overallEvaluationLoading = true
            getTeacherEvaluation(this.teacher_id, currentTab.id).then(res => {
                console.log('[loadOverallEvaluation] 接口返回:', res)
                if (res && res.code === 0) {
                    // 后端返回总体评价字符串，兼容 res.data 为字符串或对象
                    if (typeof res.data === 'string') {
                        this.overallEvaluation = res.data
                    } else if (res.data && typeof res.data.evaluation === 'string') {
                        this.overallEvaluation = res.data.evaluation
                    } else if (res.data && typeof res.data.content === 'string') {
                        this.overallEvaluation = res.data.content
                    } else {
                        this.overallEvaluation = res.data ? String(res.data) : ''
                    }
                } else {
                    console.warn('[loadOverallEvaluation] 接口返回 code 非 0，或无数据:', res)
                    this.overallEvaluation = ''
                }
            }).catch(err => {
                console.error('[loadOverallEvaluation] 获取总体评价失败', err)
                this.overallEvaluation = ''
            }).finally(() => {
                this.overallEvaluationLoading = false
            })
        },
        loadTemplates() {
            if (!this.teacher_id) {
                this.templatesError = '缺少 teacher_id 参数'
                return
            }
            this.templatesLoading = true
            this.templatesError = ''
            getTeacherTemplates(this.teacher_id).then(res => {
                if (res && res.code === 0 && res.data && res.data.length > 0) {
                    this.standardTabs = res.data.map(item => ({
                        name: item.templateName || item.name || '未命名标准',
                        id: item.id
                    }))
                    this.activeTab = 0
                    // 标准加载完成后立即加载总体评价、历史对比数据和维度树
                    this.loadOverallEvaluation()
                    this.loadHistoryCompare()
                    this.loadDimensionsTree()
                    this.loadRadarAbility()
                } else {
                    this.standardTabs = []
                    this.templatesError = (res && res.msg) || '暂无评价标准'
                }
            }).catch(err => {
                console.error('获取评价标准列表失败', err)
                this.standardTabs = []
                const msg = (err && err.toString && err.toString()) || ''
                this.templatesError = msg.includes('404') ? '评价标准接口未就绪(404)' : '评价标准加载失败'
            }).finally(() => {
                this.templatesLoading = false
            })
        },
        handleResize() {
            this.historyChartInstance && this.historyChartInstance.resize()
            this.radarChartInstance && this.radarChartInstance.resize()
        },
        initHistoryChart() {
            const chartDom = this.$refs.historyChart
            if (!chartDom) return
            this.historyChartInstance = echarts.init(chartDom)
            this.updateHistoryChart()
            window.addEventListener('resize', this.handleResize)
        },
        updateHistoryChart() {
            if (!this.historyChartInstance) return
            // 以个人记录为基准计算 Y 轴范围，避免同伴最高分异常值（如 777）拉高量程导致个人折线被压扁
            const personal = (this.historyData.personal || []).filter(v => typeof v === 'number')
            const bestRaw = this.historyData.best || []
            const avgRaw = this.historyData.average || []
            let yMin = 0
            let yMax = 100
            if (personal.length) {
                const pMin = Math.min(...personal)
                const pMax = Math.max(...personal)
                yMin = Math.max(0, Math.floor(pMin - 10))
                yMax = Math.ceil(pMax + 10)
                // 如果平均分/最优分在合理范围内，纳入 Y 轴以确保横线可见
                ;[...bestRaw, ...avgRaw].forEach(v => {
                    if (v > 0 && v <= 100) yMax = Math.max(yMax, Math.ceil(v + 5))
                })
            }
            // 对超出 Y 轴范围的最优/平均记录进行压限显示，保证“水平线”始终可见；tooltip 仍显示真实值
            const clampForDisplay = (arr) => arr.map(v => (typeof v === 'number' && v > yMax) ? (yMax - 1) : v)
            const displayData = {
                personal: this.historyData.personal || [],
                best: clampForDisplay(bestRaw),
                average: clampForDisplay(avgRaw)
            }
            const allSeries = [
                { key: 'personal', name: '个人记录', color: '#4fc3ff' },
                { key: 'best', name: '最优记录', color: '#ffd24a' },
                { key: 'average', name: '平均记录', color: '#ff8f7c' }
            ]
            const series = allSeries
                .filter(s => this.primaryFilter[s.key])
                .map(s => ({
                    name: s.name,
                    type: 'line',
                    smooth: s.key === 'personal',
                    data: displayData[s.key] || [],
                    showSymbol: s.key === 'personal',
                    lineStyle: { color: s.color, width: 2 },
                    itemStyle: { color: s.color },
                    symbolSize: 6,
                    z: s.key === 'personal' ? 10 : 5
                }))
            // tooltip 显示真实值而非压限后的显示值
            const realData = {
                '个人记录': this.historyData.personal || [],
                '最优记录': bestRaw,
                '平均记录': avgRaw
            }
            const option = {
                grid: { left: '10%', right: '5%', top: '10%', bottom: '15%' },
                xAxis: {
                    type: 'category',
                    boundaryGap: true,
                    data: this.historyData.xAxis || [],
                    axisLabel: { color: '#6f93c4', fontSize: 11, align: 'center' },
                    axisLine: { lineStyle: { color: 'rgba(63, 156, 255, 0.25)' } },
                    axisTick: { show: false }
                },
                yAxis: {
                    type: 'value',
                    min: yMin,
                    max: yMax,
                    axisLabel: { color: '#6f93c4', fontSize: 11 },
                    splitLine: { lineStyle: { color: 'rgba(63, 156, 255, 0.14)' } },
                    axisLine: { show: false },
                    axisTick: { show: false }
                },
                tooltip: {
                    trigger: 'axis',
                    backgroundColor: 'rgba(6,22,54,.95)',
                    borderColor: 'rgba(79,195,255,.5)',
                    borderWidth: 1,
                    textStyle: { color: '#cfe6ff', fontSize: 12 },
                    formatter(params) {
                        if (!params || !params.length) return ''
                        let html = params[0].axisValue + '<br/>'
                        params.forEach(p => {
                            const realArr = realData[p.seriesName] || []
                            const val = realArr[p.dataIndex] != null ? realArr[p.dataIndex] : p.value
                            html += `<span style="display:inline-block;width:10px;height:10px;background:${p.color};border-radius:50%;margin-right:5px;"></span>${p.seriesName}: ${val}<br/>`
                        })
                        return html
                    }
                },
                series
            }
            this.historyChartInstance.setOption(option, true)
        },
        loadHistoryCompare() {
            if (!this.teacher_id) return
            const currentTab = this.standardTabs[this.activeTab]
            const templateId = (currentTab && currentTab.id) || ''
            // 根据导航栈深度决定传 groupId / itemId
            const groupId = (this.historyNavStack.length >= 1) ? this.historyNavStack[0].id : ''
            const itemId = (this.historyNavStack.length >= 2) ? this.historyNavStack[1].id : ''
            getTeacherCompare(this.teacher_id, templateId, groupId, itemId).then(res => {
                console.log('[教师对比接口] 请求参数: teacherId=', this.teacher_id, 'templateId=', templateId, 'groupId=', groupId, 'itemId=', itemId)
                console.log('[教师对比接口] 返回数据：', res)
                if (!res || res.code !== 0 || !res.data) return
                const parsed = this.parseHistoryCompare(res.data)
                if (parsed) {
                    this.historyData = parsed
                    this.updateHistoryChart()
                }
            }).catch(err => {
                console.error('获取历史对比数据失败', err)
            })
        },
        parseHistoryCompare(data) {
            // 1、提取个人评课得分数组（兼容多种字段名）
            const personalArrCandidates = ['personalRecords', 'personal', 'personalScores', 'scores', 'selfScores', 'history', 'records', 'list', 'items']
            let personalArr = null
            let rawList = null
            for (const k of personalArrCandidates) {
                if (Array.isArray(data[k])) {
                    rawList = data[k]
                    break
                }
            }
            if (Array.isArray(data)) rawList = data
            if (!rawList) return null

            // 2、从 rawList 中提取得分与标签（按顺序生成“第N次”标签，数量与数组长度一致）
            const xAxis = []
            personalArr = []
            rawList.forEach((item, idx) => {
                if (typeof item === 'number') {
                    personalArr.push(item)
                } else if (item && typeof item === 'object') {
                    const score = item.score != null ? item.score
                        : (item.personal != null ? item.personal
                        : (item.personalScore != null ? item.personalScore
                        : (item.value != null ? item.value : 0)))
                    personalArr.push(this._toNum(score))
                }
                xAxis.push('第' + (idx + 1) + '次')
            })
            if (personalArr.length === 0) return null

            // 3、提取同伴最高分、全体平均分（单值 → 复制为水平线）
            const bestRaw = data.peerMaxScore != null ? data.peerMaxScore
                : (data.best != null ? data.best
                : (data.bestScore != null ? data.bestScore
                : (data.maxScore != null ? data.maxScore : null)))
            const avgRaw = data.allAvgScore != null ? data.allAvgScore
                : (data.average != null ? data.average
                : (data.averageScore != null ? data.averageScore
                : (data.avgScore != null ? data.avgScore : null)))

            const len = personalArr.length
            const expand = (v) => {
                if (Array.isArray(v)) return v.slice(0, len)
                if (v == null) return new Array(len).fill(0)
                const n = this._toNum(v)
                return new Array(len).fill(n)
            }

            return {
                xAxis,
                personal: personalArr,
                best: expand(bestRaw),
                average: expand(avgRaw)
            }
        },
        _toNum(v) {
            const n = Number(v)
            return isNaN(n) ? 0 : n
        },
        fmt(v) {
            const n = Number(v)
            if (isNaN(n)) return '—'
            return String(Math.round(n * 10) / 10)
        },
        // ==================== 历史对比筛选-维度导航 ====================
        loadDimensionsTree() {
            const currentTab = this.standardTabs[this.activeTab]
            if (!currentTab || !currentTab.id) return
            this.historyDimLoading = true
            this.historyDimTree = []
            this.historyNavStack = []
            this.historyCurrentDimId = ''
            getTemplateDimensions(currentTab.id).then(res => {
                console.log('[维度树] 返回数据:', res)
                if (res && res.code === 0 && res.data) {
                    const d = res.data
                    this.historyDimTree = Array.isArray(d) ? d : (d.list || d.dimensions || d.children || [])
                }
            }).catch(err => {
                console.error('获取维度列表失败', err)
            }).finally(() => {
                this.historyDimLoading = false
            })
        },
        selectHistoryDim(dim) {
            // 已在三级维度（栈深≥2），不再向下钻取
            if (this.historyNavStack.length >= 2) {
                this.$message.info('暂不支持更深层次维度显示')
                return
            }
            this.historyNavStack.push({ id: String(dim.id), name: dim.name })
            this.historyCurrentDimId = String(dim.id)
            this.loadHistoryCompare()
            this.loadRadarAbility()
        },
        goBackHistoryDim() {
            if (this.historyNavStack.length === 0) return
            this.historyNavStack.pop()
            this.historyCurrentDimId = this.historyNavStack.length > 0
                ? this.historyNavStack[this.historyNavStack.length - 1].id
                : ''
            this.loadHistoryCompare()
            this.loadRadarAbility()
        },
        findDimChildrenById(targetId, dimList) {
            if (!Array.isArray(dimList)) return null
            for (const dim of dimList) {
                if (String(dim.id) === String(targetId)) return dim.children || []
                if (dim.children && dim.children.length) {
                    const found = this.findDimChildrenById(targetId, dim.children)
                    if (found !== null) return found
                }
            }
            return null
        },
        // ==================== end 维度导航 ====================
        // ==================== 核心能力画像 ====================
        loadRadarAbility() {
            if (!this.teacher_id) return
            const currentTab = this.standardTabs[this.activeTab]
            const templateId = (currentTab && currentTab.id) || ''
            // 根据导航栈深度决定传 groupId / itemId
            const groupId = (this.historyNavStack.length >= 1) ? this.historyNavStack[0].id : ''
            const itemId = (this.historyNavStack.length >= 2) ? this.historyNavStack[1].id : ''
            getTeacherAbility(this.teacher_id, templateId, groupId, itemId).then(res => {
                console.log('[能力画像接口] 请求参数: teacherId=', this.teacher_id, 'templateId=', templateId, 'groupId=', groupId, 'itemId=', itemId)
                console.log('[能力画像接口] 返回数据：', res)
                if (res && (res.code === 0 || res.code === 200) && res.data && Array.isArray(res.data.dimensions)) {
                    this.radarDimensions = res.data.dimensions
                } else {
                    this.radarDimensions = []
                }
                this.updateRadarChart()
            }).catch(err => {
                console.error('获取核心能力画像数据失败', err)
                this.radarDimensions = []
                this.updateRadarChart()
            })
        },
        // ==================== end 核心能力画像 ====================
        initRadarChart() {
            const chartDom = this.$refs.radarChart
            if (!chartDom) return
            this.radarChartInstance = echarts.init(chartDom)
            this.updateRadarChart()
        },
        updateRadarChart() {
            if (!this.radarChartInstance) return
            const dims = this.radarDimensions || []
            if (dims.length === 0) {
                // 无数据时显示空雷达图
                this.radarChartInstance.setOption({
                    radar: { indicator: [{ name: '', max: 100 }] },
                    series: [{ type: 'radar', data: [] }]
                }, true)
                return
            }
            // 构建雷达图指标轴（每个维度的满分为 max，并在轴名称上显示满分）
            const indicators = dims.map(d => ({ name: d.name + '\n(' + (d.maxScore || 100) + '分)', max: d.maxScore || 100 }))
            // 三组数据系列
            const allSeries = [
                { key: 'personal', name: '个人记录', color: 'rgba(79, 195, 255, 0.25)', values: dims.map(d => d.personalScore || 0) },
                { key: 'best', name: '最优记录', color: 'rgba(255, 210, 74, 0.18)', values: dims.map(d => d.bestScore || 0) },
                { key: 'average', name: '平均记录', color: 'rgba(255, 143, 124, 0.16)', values: dims.map(d => d.avgScore || 0) }
            ]
            // 根据 primaryFilter 筛选显示的系列
            const seriesData = allSeries
                .filter(s => this.primaryFilter[s.key])
                .map(s => ({
                    value: s.values,
                    name: s.name,
                    areaStyle: { color: s.color }
                }))
            const option = {
                color: ['#4fc3ff', '#ffd24a', '#ff8f7c'],
                radar: {
                    radius: '65%',
                    center: ['50%', '50%'],
                    axisName: { color: '#a9c6e8', fontSize: 12 },
                    splitArea: {
                        areaStyle: { color: ['rgba(21, 113, 231, 0.05)', 'rgba(21, 113, 231, 0.1)'] }
                    },
                    axisLine: { lineStyle: { color: 'rgba(63, 156, 255, 0.25)' } },
                    splitLine: { lineStyle: { color: 'rgba(63, 156, 255, 0.25)' } },
                    indicator: indicators
                },
                tooltip: {
                    confine: true,
                    backgroundColor: 'rgba(6,22,54,.95)',
                    borderColor: 'rgba(79,195,255,.5)',
                    borderWidth: 1,
                    textStyle: { color: '#cfe6ff', fontSize: 12 }
                },
                series: [{ type: 'radar', data: seriesData }]
            }
            this.radarChartInstance.setOption(option, true)
        },
        sendMessage() {
            if (!this.chatInput.trim() || this.chatLoading) return
            const userMsg = this.chatInput.trim()
            this.chatMessages.push({ role: 'user', content: userMsg })
            this.chatInput = ''
            this.chatLoading = true

            // 装配 智能个性化指导 System Prompt（骨架版）—— 教师画像数据交由后端根据 teacherId/lessonId 自行注入，
            // 前端不再把 teacherContext 塞进 URL，避免 GET 请求 URL 过长被网关/Tomcat 截断。
            const systemPrompt = renderPrompt(TEACHER_GUIDE_SYSTEM_PROMPT_CORE, {})
            const username = (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.username) || 'guest'

            // 首轮需先创建 cid
            const ensureCid = this.chatCid
                ? Promise.resolve(this.chatCid)
                : axios.post('/chat/generate/id').then(res => {
                    this.chatCid = (res && res.data && res.data.cid) || ('guide_' + Date.now())
                    return this.chatCid
                  }).catch(() => {
                    this.chatCid = 'guide_' + Date.now()
                    return this.chatCid
                  })

            ensureCid.then(cid => {
                // AI 占位消息，随 SSE 流式不断追加（与议课页 aiStub 结构保持一致）
                const aiMsg = {
                    role: 'ai',
                    content: '',
                    loading: true,
                    displayText: '',
                    fullText: '',
                    typewriterActive: false,
                    typewriterTimer: null
                }
                this.chatMessages.push(aiMsg)
                this.$nextTick(() => {
                    const chatBox = this.$refs.chatMessages
                    if (chatBox) chatBox.scrollTop = chatBox.scrollHeight
                })

                let url = `/chat/${username}/${cid}?prompt=${encodeURIComponent(userMsg)}&lessonId=${this.lesson_id || ''}&teacherId=${this.teacher_id || ''}`
                if (!this.systemPromptSent) {
                    const urlWithPrompt = url + `&systemPrompt=${encodeURIComponent(systemPrompt)}`
                    // URL 长度保护：超过 7000 字符放弃 systemPrompt，避免 Tomcat 默认 maxHttpHeaderSize=8192 截断
                    if (urlWithPrompt.length <= 7000) {
                        url = urlWithPrompt
                    }
                    this.systemPromptSent = true
                }

                const es = new EventSource(url)
                const finish = (fallback) => {
                    try { es.close() } catch (e) { /* noop */ }
                    if (!aiMsg.content && !aiMsg.fullText) {
                        const text = fallback || '暂无回复，请稍后重试。'
                        aiMsg.content = text
                        aiMsg.displayText = text
                        aiMsg.fullText = text
                    }
                    aiMsg.loading = false
                    this.chatLoading = false
                    // 等打字机与语音播放完成后关闭 GIF 动画
                    this.waitForTypewriterComplete(aiMsg, () => {
                        this.waitForSpeechComplete(() => {
                            this.isAISpeaking = false
                        })
                    })
                    this.$nextTick(() => {
                        const chatBox = this.$refs.chatMessages
                        if (chatBox) chatBox.scrollTop = chatBox.scrollHeight
                    })
                }

                es.addEventListener('message', e => {
                    const d = e.data
                    if (d === '[DONE]') { finish(); return }
                    let delta = ''
                    try {
                        const data = JSON.parse(d)
                        delta = (data.choices && data.choices[0] && data.choices[0].delta && data.choices[0].delta.content) || ''
                    } catch (err) {
                        delta = d
                    }
                    if (delta) {
                        // 同步累加到 content，保证最终完整文本可读
                        aiMsg.content += delta
                        // 首个 token 到达时标记 AI 说话状态
                        if (delta && !this.isAISpeaking) {
                            this.isAISpeaking = true
                        }
                        // 启动打字机逐字显示
                        this.typewriterEffect(aiMsg, delta)
                        // 入语音缓冲区，按句切分后进队播放——语音开始播时会触发 isVideoPlaying=true 切换到 GIF
                        this.speechBuffer += delta
                        this.checkAndSpeakFromBuffer()
                        this.$nextTick(() => {
                            const chatBox = this.$refs.chatMessages
                            if (chatBox) chatBox.scrollTop = chatBox.scrollHeight
                        })
                    }
                })
                es.addEventListener('error', () => { finish('网络异常，请稍后重试。') })
            }).catch(() => {
                this.chatMessages.push({ role: 'ai', content: '网络异常，请稍后重试。' })
                this.chatLoading = false
            })
        },

        // ===== 下面为与议课页一致的智能体图片 / 动画 / 语音 / 打字机 流程处理 =====

        playAiVideo() {
            // 使用 GIF 动画，直接设置播放状态
            this.isVideoPlaying = true
        },

        pauseAiVideo() {
            // 隐藏 GIF 动画
            this.isVideoPlaying = false
        },

        // 加载并缓存语音列表，优先查找中文男声
        loadAndCacheVoices() {
            if (!this.speechSynthesis) return
            const voices = this.speechSynthesis.getVoices()
            this.availableVoices = voices
            const zhVoices = voices.filter(voice => voice.lang && voice.lang.includes('zh'))
            const maleKeywords = ['kangkang', 'yunjian', 'xiaogang', 'male', '男', 'boy', 'man']
            let foundMaleVoice = null
            for (const keyword of maleKeywords) {
                foundMaleVoice = zhVoices.find(voice =>
                    voice.name.toLowerCase().includes(keyword) ||
                    (voice.voiceURI || '').toLowerCase().includes(keyword)
                )
                if (foundMaleVoice) break
            }
            if (!foundMaleVoice && zhVoices.length > 0) {
                const femaleKeywords = ['huihui', 'yaoyao', 'xiaoxiao', 'xiaoyan', 'female', '女', 'girl', 'woman']
                foundMaleVoice = zhVoices.find(voice => {
                    const nameLower = voice.name.toLowerCase()
                    const uriLower = (voice.voiceURI || '').toLowerCase()
                    return !femaleKeywords.some(keyword =>
                        nameLower.includes(keyword) || uriLower.includes(keyword)
                    )
                })
            }
            this.maleVoiceReady = foundMaleVoice || zhVoices[0] || null
        },

        // 按句切分语音缓冲区并进队
        checkAndSpeakFromBuffer() {
            const punctuationPattern = /[。！？,.?!，！？.]/
            const matchIndex = this.speechBuffer.search(punctuationPattern)
            if (matchIndex !== -1) {
                const endIndex = matchIndex + 1
                const sentence = this.speechBuffer.substring(0, endIndex)
                this.speechBuffer = this.speechBuffer.substring(endIndex)
                this.speechQueue.push(sentence)
                if (!this.isPlayingSpeech) {
                    this.playNextSpeech()
                }
            } else if (this.speechBuffer.length >= 30) {
                const sentence = this.speechBuffer.substring(0, 30)
                this.speechBuffer = this.speechBuffer.substring(30)
                this.speechQueue.push(sentence)
                if (!this.isPlayingSpeech) {
                    this.playNextSpeech()
                }
            }
        },

        // 从队列取出下一句语音播放，同时控制 isVideoPlaying 切换 GIF
        playNextSpeech() {
            if (this.speechQueue.length === 0) {
                this.isPlayingSpeech = false
                this.isVideoPlaying = false
                return
            }
            this.isPlayingSpeech = true
            this.isVideoPlaying = true
            const text = this.speechQueue.shift()
            this.speakText(text, () => {
                this.$nextTick(() => {
                    this.playNextSpeech()
                })
            })
        },

        // 调用 Web Speech API 播放一句文本
        speakText(text, callback) {
            if (!this.speechSynthesis) {
                if (callback) callback()
                return
            }
            const cleanText = this.cleanTextForSpeech(text)
            if (!cleanText || cleanText.trim() === '') {
                if (callback) callback()
                return
            }
            this.speechSynthesis.cancel()
            if (this.availableVoices.length === 0) {
                this.loadAndCacheVoices()
            }
            const utterance = new SpeechSynthesisUtterance(cleanText)
            if (this.maleVoiceReady) {
                utterance.voice = this.maleVoiceReady
                utterance.voiceURI = this.maleVoiceReady.voiceURI
                const femaleVoiceNames = ['huihui', 'yaoyao', 'xiaoxiao', 'xiaoyan']
                const isFemaleVoice = femaleVoiceNames.some(name =>
                    this.maleVoiceReady.name.toLowerCase().includes(name)
                )
                if (isFemaleVoice) {
                    utterance.pitch = 1.2
                    utterance.rate = 2.0
                } else {
                    utterance.pitch = 1.0
                    utterance.rate = 2.0
                }
            } else {
                utterance.pitch = 1.0
                utterance.rate = 2.0
            }
            utterance.lang = 'zh-CN'
            utterance.volume = 1.0
            utterance.onend = () => {
                this.currentUtterance = null
                if (callback) callback()
            }
            utterance.onerror = () => {
                this.currentUtterance = null
                if (callback) callback()
            }
            this.currentUtterance = utterance
            this.speechSynthesis.speak(utterance)
        },

        stopSpeaking() {
            if (this.speechSynthesis) {
                this.speechSynthesis.cancel()
                this.currentUtterance = null
            }
        },

        waitForSpeechComplete(callback) {
            const checkCompletion = () => {
                if (!this.speechSynthesis || this.speechSynthesis.speaking === false) {
                    callback()
                } else {
                    setTimeout(checkCompletion, 100)
                }
            }
            checkCompletion()
        },

        cleanTextForSpeech(text) {
            if (!text) return ''
            return text
                .replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]/gu, '')
                .replace(/\s+/g, ' ')
                .trim()
        },

        // 逐字打字机效果，与议课页保持一致
        typewriterEffect(aiMsg, newText) {
            if (!aiMsg.fullText) {
                aiMsg.fullText = ''
            }
            aiMsg.fullText += newText
            if (aiMsg.typewriterTimer) {
                clearTimeout(aiMsg.typewriterTimer)
            }
            aiMsg.typewriterActive = true
            let currentIndex = (aiMsg.displayText || '').length
            const targetText = aiMsg.fullText
            const typeNextChar = () => {
                if (currentIndex < targetText.length) {
                    aiMsg.displayText = targetText.substring(0, currentIndex + 1)
                    currentIndex++
                    aiMsg.typewriterTimer = setTimeout(typeNextChar, 30)
                    this.$nextTick(() => {
                        const chatBox = this.$refs.chatMessages
                        if (chatBox) chatBox.scrollTop = chatBox.scrollHeight
                    })
                } else {
                    aiMsg.typewriterActive = false
                }
            }
            typeNextChar()
        },

        waitForTypewriterComplete(aiMsg, callback) {
            const checkCompletion = () => {
                if (!aiMsg.typewriterActive) {
                    callback()
                } else {
                    setTimeout(checkCompletion, 50)
                }
            }
            checkCompletion()
        }
    }
}
</script>

<style scoped lang="less">
@c-blue: #4fc3ff;
@c-gold: #ffd24a;
@c-green: #35e0b0;
@c-purple: #a78bfa;
@c-avg: #ff8f7c;
@c-text: #cfe6ff;
@c-sub: #7ea6d9;

.tp-page {
  width: 100%;
  padding: 0 4px 18px;
  box-sizing: border-box;
  color: @c-text;
  font-size: 13px;
}

/* ===== 身份条 ===== */
.tp-idbar {
  display: flex;
  align-items: center;
  gap: 20px;
  background: linear-gradient(180deg, rgba(16, 55, 130, .4), rgba(6, 20, 50, .55));
  border: 1px solid rgba(63, 156, 255, .3);
  border-radius: 10px;
  padding: 15px 22px;
  margin-bottom: 14px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute; left: -60px; top: -80px;
    width: 260px; height: 260px; border-radius: 50%;
    background: radial-gradient(circle, rgba(42, 125, 232, .28), transparent 68%);
  }
}

.tp-avatar-wrap { position: relative; flex: none; width: 78px; height: 96px; }

.tp-avatar {
  width: 78px; height: 96px; border-radius: 8px; position: relative; z-index: 2;
  display: flex; align-items: center; justify-content: center;
  font-size: 30px; font-weight: 500; color: #eaf6ff; letter-spacing: 2px;
  background: linear-gradient(180deg, rgba(42, 125, 232, .55), rgba(12, 42, 95, .85));
  border: 1px solid rgba(79, 195, 255, .6);
  box-shadow: 0 0 20px rgba(42, 125, 232, .5), inset 0 0 22px rgba(0, 30, 80, .7);
}

.tp-avatar-img {
  width: 78px; height: 96px; border-radius: 8px; position: relative; z-index: 2;
  object-fit: cover;
  border: 1px solid rgba(79, 195, 255, .6);
  box-shadow: 0 0 20px rgba(42, 125, 232, .5);
}

.tp-avatar-ring {
  position: absolute; inset: -4px; border-radius: 11px; z-index: 1;
  border: 1px solid rgba(79, 195, 255, .35);
  box-shadow: 0 0 16px rgba(79, 195, 255, .35);
}

.tp-idmain { flex: 1; min-width: 0; position: relative; z-index: 2; }

.tp-name {
  font-size: 24px; font-weight: 500; color: #eaf6ff; letter-spacing: 2px;
  text-shadow: 0 0 16px rgba(64, 156, 255, .6); margin-bottom: 10px;

  .tp-account {
    font-size: 13px; font-family: Consolas, monospace; color: #6f93c4;
    letter-spacing: 0; font-weight: 400; margin-left: 10px;
  }
}

.tp-chips { display: flex; flex-wrap: wrap; gap: 8px; }

.tp-chip {
  font-size: 12px; padding: 3px 12px; border-radius: 20px;
  border: 1px solid rgba(63, 156, 255, .4);
  background: rgba(27, 98, 224, .16); color: #9ec7ff;

  &.sub { color: @c-green; border-color: rgba(53, 224, 176, .45); background: rgba(53, 224, 176, .1); }
  &.tmpl { color: @c-purple; border-color: rgba(167, 139, 250, .45); background: rgba(167, 139, 250, .12); }
  &.lessons { color: @c-gold; border-color: rgba(255, 210, 74, .45); background: rgba(255, 210, 74, .1); }
}

.tp-idright {
  flex: none; display: flex; flex-direction: column; align-items: flex-end; gap: 10px;
  position: relative; z-index: 2; max-width: 46%;
}

.tp-std-tabs { display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; }

.tp-std-tab {
  font-size: 13px; padding: 6px 16px; border-radius: 6px; cursor: pointer;
  border: 1px solid rgba(44, 88, 125, .6); color: @c-sub;
  transition: all .2s; white-space: nowrap; max-width: 260px;
  overflow: hidden; text-overflow: ellipsis;

  &:hover { border-color: rgba(79, 195, 255, .6); color: #9fc7f0; }

  &.active {
    color: #eaf6ff; font-weight: 500;
    background: linear-gradient(180deg, rgba(42, 125, 232, .6), rgba(27, 98, 224, .25));
    border-color: @c-blue;
    box-shadow: 0 0 12px rgba(42, 125, 232, .55);
  }
}

.tp-tabs-empty { font-size: 12px; color: #5f8fd0; display: flex; align-items: center; gap: 6px; }

.tp-update { font-size: 11px; color: #5f8fd0; b { color: @c-sub; font-weight: 400; } }

/* ===== KPI ===== */
.tp-kpis {
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px; margin-bottom: 14px;
}

.tp-kpi {
  display: flex; align-items: center; gap: 14px;
  background: linear-gradient(180deg, rgba(16, 55, 130, .35), rgba(8, 25, 60, .35));
  border: 1px solid rgba(79, 195, 255, .28);
  border-radius: 8px; padding: 13px 18px; position: relative; overflow: hidden;

  &::after {
    content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
    background: var(--c); box-shadow: 0 0 10px var(--c);
  }

  &::before {
    content: ""; position: absolute; right: -30px; top: -30px;
    width: 90px; height: 90px; border-radius: 50%;
    background: radial-gradient(circle, rgba(79, 195, 255, .18), transparent 70%);
  }

  .ring {
    flex: none; width: 46px; height: 46px; border-radius: 50%;
    background: radial-gradient(circle, rgba(8, 25, 60, .9), rgba(8, 25, 60, .9));
    border: 1px solid var(--c);
    box-shadow: 0 0 14px rgba(79, 195, 255, .25), inset 0 0 12px rgba(79, 195, 255, .18);
    display: flex; align-items: center; justify-content: center;
  }

  .info {
    min-width: 0;

    .label { font-size: 12px; color: @c-sub; margin-bottom: 3px; }

    .value {
      font-size: 24px; font-weight: 500;
      font-family: "DIN Alternate", "Segoe UI", Consolas, sans-serif;
      background: linear-gradient(180deg, #ffffff, var(--c));
      -webkit-background-clip: text; background-clip: text; color: transparent;
      white-space: nowrap;
      small { font-size: 12px; margin-left: 3px; }
    }

    .trend {
      font-size: 11px; margin-top: 3px; color: #8fb4dd;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
  }
}

/* ===== 卡片 ===== */
.tp-card {
  background: linear-gradient(180deg, rgba(13, 40, 95, .35), rgba(5, 18, 45, .55));
  border: 1px solid rgba(63, 156, 255, .28);
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: inset 0 0 24px rgba(20, 70, 170, .15);
  margin-bottom: 14px;
  min-width: 0;
}

.tp-flex1 { flex: 1; min-height: 0; display: flex; flex-direction: column; }

.tp-sectitle {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 500; color: #dceeff; letter-spacing: 1px;
  margin-bottom: 12px; padding-bottom: 8px;
  border-bottom: 1px solid transparent;
  border-image: linear-gradient(90deg, rgba(63, 156, 255, .8), transparent) 1;

  &::before {
    content: ""; width: 8px; height: 8px; background: var(--accent, #3f9cff);
    transform: rotate(45deg); box-shadow: 0 0 8px var(--accent, #3f9cff);
  }

  .more {
    margin-left: auto; font-size: 12px; color: #5f8fd0; font-weight: 400;
    cursor: pointer; white-space: nowrap;
    &:hover { color: @c-blue; }
  }
}

.tp-grid { display: grid; grid-template-columns: 27fr 42fr 31fr; gap: 14px; }
.tp-col { display: flex; flex-direction: column; min-width: 0; }

/* 基本信息 */
.tp-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }

.tp-info-cell {
  background: linear-gradient(135deg, rgba(10, 50, 100, .5), rgba(10, 40, 85, .25));
  border: 1px solid rgba(63, 156, 255, .22);
  border-left: 2px solid rgba(79, 195, 255, .6);
  border-radius: 4px; padding: 9px 12px;
  min-width: 0;

  .k { font-size: 11px; color: @c-sub; margin-bottom: 3px; }

  .v {
    font-size: 15px; color: #eaf6ff; font-weight: 500;
    font-family: "DIN Alternate", Consolas, sans-serif;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  .v.plain { font-family: inherit; font-size: 14px; }
  &.wide { grid-column: 1 / -1; }
}

/* 总体评价 */
.tp-eval-text {
  font-size: 13px; line-height: 1.95; color: #c4dcf5; text-align: justify;
  white-space: pre-wrap;
  flex: 1; min-height: 0; overflow-y: auto; padding-right: 4px;
}

/* 图表 */
.tp-chart { position: relative; width: 100%; height: 250px; }
.tp-chart-history { height: 200px; }
.tp-history-wrap { position: relative; }

.tp-legend {
  display: flex; justify-content: center; gap: 20px; padding: 8px 0 2px; font-size: 12px;

  .item { display: flex; align-items: center; gap: 6px; color: #a9c6e8; }
  .lc { width: 18px; height: 3px; border-radius: 2px; display: inline-block; }
  .lc-personal { background: @c-blue; box-shadow: 0 0 8px @c-blue; }
  .lc-best { background: @c-gold; box-shadow: 0 0 8px @c-gold; }
  .lc-avg { background: @c-avg; box-shadow: 0 0 8px @c-avg; }
}

/* 维度筛选浮层 */
.tp-filter-overlay {
  position: absolute; right: 4px; top: 4px; width: 200px; max-height: 88%;
  overflow-y: auto; z-index: 5;
  background: rgba(6, 22, 54, .96);
  border: 1px solid rgba(63, 156, 255, .45);
  border-radius: 8px; padding: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .45);
}

.tp-filter-title {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px; color: #bfe0ff; margin-bottom: 8px; padding-bottom: 6px;
  border-bottom: 1px solid rgba(63, 156, 255, .22);

  .tp-filter-close { cursor: pointer; color: #5f8fd0; &:hover { color: @c-blue; } }
}

.tp-dim-loading, .tp-dim-empty { font-size: 12px; color: #5f8fd0; padding: 8px 0; text-align: center; }

.tp-dim-list { display: flex; flex-direction: column; gap: 4px; }

.tp-dim-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 10px; border-radius: 5px; cursor: pointer;
  font-size: 12.5px; color: #a9c6e8;
  background: rgba(13, 40, 95, .35);
  border: 1px solid rgba(63, 156, 255, .18);
  transition: all .18s;

  &:hover { border-color: rgba(79, 195, 255, .6); color: #eaf6ff; }
  &.active { color: #eaf6ff; background: rgba(27, 98, 224, .35); border-color: @c-blue; }

  .dim-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dim-arrow { color: #4f77ab; }
}

.tp-filter-back {
  margin-top: 8px; padding-top: 6px; font-size: 12px; color: @c-blue; cursor: pointer;
  border-top: 1px dashed rgba(63, 156, 255, .22);
  &:hover { color: #7fd8ff; }
}

/* 智能个性化指导 */
.tp-ai-wrap { display: flex; flex-direction: column; flex: 1; min-height: 0; }

.tp-ai-stage {
  position: relative; height: 168px; border-radius: 8px; overflow: hidden; margin-bottom: 12px;
  background: radial-gradient(circle at 50% 30%, rgba(42, 125, 232, .35), rgba(6, 20, 50, .7) 70%);
  border: 1px solid rgba(63, 156, 255, .3);
  display: flex; align-items: flex-end; justify-content: center;
}

.tp-ai-img { max-height: 100%; max-width: 100%; object-fit: contain; }

.tp-ai-badge {
  position: absolute; top: 10px; left: 12px; z-index: 3;
  font-size: 11px; color: #6fc4ff; padding: 3px 10px; border-radius: 20px;
  border: 1px solid rgba(79, 195, 255, .5); background: rgba(6, 22, 54, .7);
}

.tp-chat-area {
  flex: 1; display: flex; flex-direction: column; min-height: 0;
  border: 1px solid rgba(63, 156, 255, .25); border-radius: 8px; background: rgba(4, 16, 40, .5);
}

.tp-chat-messages {
  flex: 1; overflow-y: auto; padding: 12px;
  display: flex; flex-direction: column; gap: 12px; min-height: 160px;
}

.tp-msg {
  display: flex; gap: 8px; align-items: flex-start;

  .m-avatar {
    flex: none; width: 26px; height: 26px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; font-size: 12px;
    background: radial-gradient(circle at 35% 30%, #2a7de8, #123a7d);
    border: 1px solid rgba(79, 195, 255, .5); color: #eaf6ff;
    overflow: hidden;
    .m-avatar-img { width: 100%; height: 100%; object-fit: cover; }
  }

  .m-body {
    font-size: 13px; line-height: 1.7; color: @c-text; max-width: 84%;
    background: rgba(0, 40, 80, .55); border: 1px solid rgba(44, 88, 125, .5);
    border-radius: 8px; padding: 8px 12px; white-space: pre-wrap;
  }

  &.user {
    flex-direction: row-reverse;
    .m-body { background: rgba(27, 98, 224, .4); border-color: rgba(79, 195, 255, .55); color: #eaf6ff; }
  }
}

.tp-chat-input {
  display: flex; gap: 8px; padding: 10px;
  border-top: 1px solid rgba(63, 156, 255, .18);

  /deep/ .el-input__inner {
    background: rgba(6, 22, 54, .8);
    border: 1px solid rgba(63, 156, 255, .4);
    color: @c-text;
    &::placeholder { color: #4f77ab; }
  }

  /deep/ .el-button--primary {
    background: linear-gradient(180deg, #2a7de8, #1b62e0);
    border-color: @c-blue;
    box-shadow: 0 0 12px rgba(42, 125, 232, .6);
  }
}

/* 滚动条 */
.tp-page ::-webkit-scrollbar { width: 6px; height: 6px; }
.tp-page ::-webkit-scrollbar-thumb { background: rgba(63, 156, 255, .35); border-radius: 3px; }
.tp-page ::-webkit-scrollbar-track { background: transparent; }
</style>
